import { cn, NoddiIcon } from 'noddi-ui-common';
import { forwardRef } from 'react';

const pulseAnimation = `
  @keyframes pulse {
    0% {transform: scale(0.1, 0.1); opacity: 0.0;}
    50% {opacity: 1.0;}
    100% {transform: scale(1.2, 1.2); opacity: 0.0;}
  }
`;

const LiveMarker = ({ className }: { className?: string }) => (
  <div className={cn('relative size-[15px] min-h-[15px] min-w-[15px] rounded-full bg-emerald-600', className)}>
    <div
      className='absolute top-[-5px] left-[-5px] size-[25px] rounded-full border-[3px] border-emerald-600'
      style={{
        animation: 'pulse 1s ease-out infinite'
      }}
    />
    <style>{pulseAnimation}</style>
  </div>
);

export interface StepperStep {
  label: string;
  description?: string;
  key: string | string[];
  content?: React.ReactNode;
  isLive?: boolean;
}

export interface NoddiStepperProps {
  steps: StepperStep[];
  activeStep: number;
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

export const NoddiStepper = forwardRef<HTMLDivElement, NoddiStepperProps>(
  ({ steps, activeStep, orientation = 'vertical', className }, ref) => {
    const isVertical = orientation === 'vertical';

    return (
      <div ref={ref} className={cn('flex', isVertical ? 'flex-col' : 'flex-row', className)}>
        {steps.map((step, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          const isLast = index === steps.length - 1;

          return (
            <div key={step.label} className={cn('flex', isVertical ? 'flex-row' : 'flex-row', 'items-start')}>
              <div className='mt-2 flex flex-col items-center'>
                {step.isLive ? (
                  // Same box as the numbered markers, or this row's column narrows and its line and text shift left.
                  <div className='flex size-5 items-center justify-center'>
                    <LiveMarker />
                  </div>
                ) : (
                  <div
                    className={cn(
                      'flex size-5 items-center justify-center rounded-full border-2 transition-colors',
                      isCompleted
                        ? 'border-primary bg-primary text-primary-foreground'
                        : isActive
                          ? 'border-card-selected-background bg-card-selected-background text-card-selected-foreground'
                          : 'border-muted bg-background text-muted-foreground'
                    )}
                  >
                    {isCompleted ? (
                      <NoddiIcon name='Check' />
                    ) : (
                      <span className='text-sm font-medium'>{index + 1}</span>
                    )}
                  </div>
                )}

                {/* Connector line - only show in vertical mode */}
                {!isLast && isVertical && (
                  <div
                    className={cn(
                      'mt-2 w-0.5 transition-colors',
                      isVertical ? 'h-8' : 'h-0.5 w-8',
                      isCompleted ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </div>

              {/* Step content */}
              <div className={cn('flex-1', isVertical ? 'ml-4' : 'ml-2')}>
                <div className={step.content && !step.description ? 'mb-0' : 'mb-2'}>
                  <h3
                    className={cn(
                      'font-medium transition-colors',
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {step.label}
                  </h3>
                  {step.description && (
                    <p
                      className={cn(
                        'text-sm transition-colors',
                        isActive ? 'text-foreground' : 'text-muted-foreground'
                      )}
                    >
                      {step.description}
                    </p>
                  )}
                </div>

                {/* Optional step content */}
                {step.content}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);

NoddiStepper.displayName = 'NoddiStepper';
